import React from 'react';
import { Card, Text, makeStyles, tokens } from '@fluentui/react-components';
import { DrillDownItem } from '../types';

interface DrillDownViewProps {
  items: DrillDownItem[];
  title?: string;
  onItemClick: (item: DrillDownItem) => void;
  searchQuery?: string;
}

const useStyles = makeStyles({
  grid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))',
    gap: tokens.spacingVerticalL,
    marginTop: tokens.spacingVerticalL,
  },
  card: {
    cursor: 'pointer',
    transition: 'transform 0.2s, box-shadow 0.2s',
    ':hover': {
      transform: 'translateY(-4px)',
      boxShadow: tokens.shadow16,
    },
  },
  title: {
    marginBottom: tokens.spacingVerticalL,
  },
  emptyState: {
    textAlign: 'center',
    padding: tokens.spacingVerticalXXL,
    color: tokens.colorNeutralForeground3,
  },
});

const DrillDownView: React.FC<DrillDownViewProps> = ({ 
  items, 
  title, 
  onItemClick, 
  searchQuery = '' 
}) => {
  const styles = useStyles();

  const filteredItems = searchQuery
    ? items.filter((item) =>
        item.label.toLowerCase().includes(searchQuery.toLowerCase())
      )
    : items;

  const handleItemClick = (item: DrillDownItem): void => {
    onItemClick(item);
  };

  if (filteredItems.length === 0) {
    return (
      <div className={styles.emptyState}>
        <Text>Failed to fetch items. We are working towards a fix.</Text>
      </div>
    );
  }

  return (
    <div>
      {title && (
        <Text as="h2" size={600} weight="semibold" className={styles.title}>
          {title}
        </Text>
      )}
      <div className={styles.grid}>
        {filteredItems.map((item, index) => (
          <Card
            key={index}
            className={styles.card}
            onClick={() => handleItemClick(item)}
          >
            <div style={{ padding: '1.5rem' }}>
              <Text as="h3" size={500} weight="semibold" style={{ marginBottom: '0.5rem', display: 'block' }}>
                {item.label}
              </Text>
              {item.count !== undefined && (
                <Text size={400} style={{ color: 'var(--colorNeutralForeground3)', display: 'block' }}>
                  {item.count} {item.count === 1 ? 'item' : 'items'}
                </Text>
              )}
            </div>
          </Card>
        ))}
      </div>
    </div>
  );
};

export default DrillDownView;

